import * as SensorsIcon from '@assets/icons/sprite_icons/sensor';
import { BaseSensorIconKeys } from '@assets/icons/sprite_icons/sensor/sencorIcon.types';
import {
  ColorLabelEnum,
  extendedSensorIconShorthand,
  extendedSensorShortHand,
  iconColor,
} from '@utils/map/map.constants';
import {
  getSensorColorLabel,
  hasSpecificState,
  isAutomaton,
  isCircuitBreakerEnabled,
  isEmergencyStopEnabled,
  isSensorConfiguration,
  isSensorOrSubSensorConfigurationOfType,
  isSensorOrSubSensorStatusOfType,
} from '@utils/sensors/sensors.utils';
import { capitalize, partition } from 'lodash';
import { FunctionComponent, SVGProps } from 'react';

import { Coordinates } from '@/types/commons/commons.types';
import { MapFilters } from '@/types/filters.types';
import {
  SensorDisplayedOnMap,
  SensorLikeIconBasicInfo,
  SENSORS_NOT_DISPLAYED_ON_MAP,
  SplitMonitoringSensors,
  SubSensorInfo,
} from '@/types/map.types';
import {
  EXTENDED_RADAR_SENSORS,
  SensorConfigurationByType,
  SensorOrSubSensorConfiguration,
  SENSORS_WITH_SUBSENSOR,
  SensorWithSubSensorType,
} from '@/types/sensor/configuration.types';
import {
  ActiveMonitoringSensor,
  ActiveMonitoringSensorByType,
  MonitoringSensor,
  MonitoringSensorByType,
} from '@/types/sensor/monitoring.types';
import {
  AutomatonCabinetStatus,
  ConnectorStatusEnum,
  EffectiveSensorStatusEnum,
  ExtendedSensor,
  HealthStatusEnum,
  SensorTypeEnum,
  SpecificStateEnum,
  SubSensorTypeEnum,
} from '@/types/sensor/sensors';
import { SensorOrSubSensorStatus } from '@/types/sensor/status.types';

export function getSensorIconColor(
  active: boolean,
  maintenance: boolean,
  type: ExtendedSensor,
  specificState: SpecificStateEnum | null | undefined,
  status?: EffectiveSensorStatusEnum | null,
): string {
  return iconColor[getSensorColorLabel(active, maintenance, type, specificState, status)];
}

function getRadarState(state: SpecificStateEnum | null | undefined, type: ExtendedSensor, isIconKey: boolean) {
  const emissionOff = isIconKey ? 'EmissionOff' : '-emissionOff';
  if (type === SensorTypeEnum.XENTA) {
    switch (state) {
      case SpecificStateEnum.ROTATING:
        return emissionOff;
      case SpecificStateEnum.EMITTING:
        return '';
      case null:
      case undefined:
        return isIconKey ? 'EmissionOffRotationOff' : '-emissionOff-rotationOff';
      default:
        return '';
    }
  } else {
    return state ? '' : emissionOff;
  }
}

function getSectionState(state: SpecificStateEnum | null | undefined, isIconKey: boolean) {
  switch (state) {
    case SpecificStateEnum.HT_DETECTION:
      return isIconKey ? 'HtDetection' : '-ht-detection';
    case SpecificStateEnum.EFFRACTION:
      return isIconKey ? 'Effraction' : '-effraction';
    case SpecificStateEnum.SLEEP:
      return isIconKey ? 'Sleep' : '-sleep';
    default:
      return '';
  }
}

export function getTechnicalStateName(
  state: SpecificStateEnum | null | undefined,
  type: ExtendedSensor,
  status: EffectiveSensorStatusEnum | undefined,
  active = true,
  isIconKey = false,
): string {
  if (
    active &&
    status &&
    status !== ConnectorStatusEnum.UNREACHABLE &&
    EXTENDED_RADAR_SENSORS.some((t) => t === type)
  ) {
    return getRadarState(state, type, isIconKey);
  }
  if (type === SubSensorTypeEnum.AUTOMATON_SEGMENT) {
    return getSectionState(state, isIconKey);
  }
  switch (state) {
    case SpecificStateEnum.ROUND:
      return isIconKey ? 'Round' : '-round';
    case SpecificStateEnum.TRACKING:
      return isIconKey ? 'Tracking' : '-tracking';
    default:
      return '';
  }
}

export function getSensorSpriteIconName({ active, maintenance, type, status, state }: SensorLikeIconBasicInfo): string {
  if (active && maintenance) {
    return `icSymb-${extendedSensorIconShorthand[type]}-work-${iconColor[ColorLabelEnum.ORANGE]}`;
  } else {
    const technicalStatusName = getTechnicalStateName(state, type, status, active);
    return `icSymb-${extendedSensorIconShorthand[type]}${technicalStatusName}-${getSensorIconColor(
      active,
      maintenance,
      type,
      state,
      status,
    )}`;
  }
}

export function getIconBasicInfoFromSensor({
  active,
  configuration,
  type,
  status,
}: MonitoringSensorByType<SensorDisplayedOnMap>): SensorLikeIconBasicInfo {
  return {
    active,
    maintenance: configuration.maintenance,
    type,
    state: status && hasSpecificState(status) ? status.specificState : undefined,
    status: status?.effectiveStatus,
  };
}

export function getIconBasicInfoFromSubSensorInfo({
  parentSensorConfig,
  subSensorConfig,
  subSensorStatus,
}: SubSensorInfo<SensorWithSubSensorType>): SensorLikeIconBasicInfo {
  return {
    active: parentSensorConfig.active && subSensorConfig.active,
    maintenance: parentSensorConfig.maintenance || subSensorConfig.maintenance,
    type: subSensorConfig.type,
    state: subSensorStatus && hasSpecificState(subSensorStatus) ? subSensorStatus.specificState : undefined,
    status: subSensorStatus?.effectiveStatus,
  };
}

function getCabinetSpecialStatus(status: AutomatonCabinetStatus) {
  if (status.effectiveStatus !== HealthStatusEnum.DEGRADED && status.specificState !== SpecificStateEnum.SLEEP) {
    return '';
  }
  const statusArray: string[] = [];
  if (isCircuitBreakerEnabled(status)) {
    statusArray.push('-cb');
  }
  if (isEmergencyStopEnabled(status)) {
    statusArray.push('-es');
  }
  return statusArray.join('');
}

function getSubSensorSpecificState(subSensorInfo: SubSensorInfo<SensorWithSubSensorType>) {
  const { active, maintenance, state, type, status } = getIconBasicInfoFromSubSensorInfo(subSensorInfo);
  if (active && maintenance) {
    return '-work';
  }
  if (subSensorInfo.subSensorStatus) {
    if (isSensorOrSubSensorStatusOfType(subSensorInfo.subSensorStatus, SubSensorTypeEnum.AUTOMATON_CABINET)) {
      return getCabinetSpecialStatus(subSensorInfo.subSensorStatus);
    }
  }
  return getTechnicalStateName(state, type, status, active);
}

export function getSubSensorSpriteIconName(subSensorInfo: SubSensorInfo<SensorWithSubSensorType>): string {
  const { active, maintenance, status, type, state } = getIconBasicInfoFromSubSensorInfo(subSensorInfo);
  const specificState = getSubSensorSpecificState(subSensorInfo);
  return `icSymb-${extendedSensorIconShorthand[type]}${specificState}-${getSensorIconColor(active, maintenance, type, state, status)}`;
}

export function getSensorIconKey(
  type: ExtendedSensor,
  active: boolean,
  maintenance: boolean,
  state: SpecificStateEnum | null | undefined,
  status?: EffectiveSensorStatusEnum | null,
  skipTechnicalName = false,
): BaseSensorIconKeys {
  if (active && maintenance) {
    return `${capitalize(extendedSensorShortHand[type])}WorkOr` as BaseSensorIconKeys;
  } else {
    const technicalStatusName =
      status && !skipTechnicalName ? getTechnicalStateName(state, type, status, active, true) : '';
    return `${capitalize(extendedSensorShortHand[type])}${technicalStatusName}${capitalize(
      getSensorIconColor(active, maintenance, type, state, status),
    )}` as BaseSensorIconKeys;
  }
}

export function getSensorIconByKey(
  key: BaseSensorIconKeys,
  size: 'md' | 'lg' | 'sm',
): FunctionComponent<SVGProps<SVGSVGElement>> {
  switch (size) {
    case 'lg':
      return SensorsIcon[`${key}L`];
    case 'sm':
    case 'md':
      return SensorsIcon[`${key}M`];
  }
}

export function isSensorWithSubSensors(
  sensor: SensorOrSubSensorConfiguration,
): sensor is SensorConfigurationByType<SensorWithSubSensorType> {
  if (!isSensorConfiguration(sensor)) {
    return false;
  }
  return (SENSORS_WITH_SUBSENSOR as readonly SensorTypeEnum[]).includes(sensor.type);
}

export function isMonitoringSensorWithSubSensors(
  sensor: MonitoringSensor,
): sensor is MonitoringSensorByType<SensorWithSubSensorType> {
  return isSensorWithSubSensors(sensor.configuration);
}

export function splitSensors(
  monitoringSensors: MonitoringSensor[],
  mapFilters: Partial<MapFilters>,
): SplitMonitoringSensors {
  const tmp = partition<MonitoringSensor, MonitoringSensorByType<SensorWithSubSensorType>>(
    monitoringSensors,
    isMonitoringSensorWithSubSensors,
  );
  let withSubSensors = tmp[0];
  let regularSensors = tmp[1] as MonitoringSensorByType<SensorDisplayedOnMap>[];
  const automatons = withSubSensors.filter(isAutomaton);

  if (mapFilters['displayPerimeterSensorAutomaton']) {
    regularSensors = [...regularSensors, ...automatons];
  }

  if (!mapFilters['displayPerimeterSensorCabinet']) {
    withSubSensors = withSubSensors.filter(
      (ms) => !isSensorOrSubSensorConfigurationOfType(ms.configuration, SensorTypeEnum.AUTOMATON),
    );
  }

  return { regularSensors, withSubSensors };
}

export function isConfigDisplayedOnMap(
  sensor: MonitoringSensor,
): sensor is MonitoringSensorByType<SensorDisplayedOnMap> {
  return !(SENSORS_NOT_DISPLAYED_ON_MAP as readonly SensorTypeEnum[]).includes(sensor.type);
}

export function isSensorDisplayedOnMap(
  monitoringSensor: ActiveMonitoringSensor,
): monitoringSensor is ActiveMonitoringSensorByType<SensorDisplayedOnMap> {
  return isConfigDisplayedOnMap(monitoringSensor);
}

export function getSensorOrSubSensorPositionCoordinates(
  sensor: SensorOrSubSensorConfiguration,
  status?: SensorOrSubSensorStatus | null,
): Coordinates {
  if (status && 'position' in status) {
    return { latitude: status.position.latitude, longitude: status.position.longitude, type: '2D' };
  } else if (isSensorConfiguration(sensor)) {
    return { latitude: sensor.sensorPosition.latitude, longitude: sensor.sensorPosition.longitude, type: '2D' };
  } else {
    return { latitude: sensor.position.latitude, longitude: sensor.position.longitude, type: '2D' };
  }
}

export function getSensorOrSubSensorAltitude(sensor: SensorOrSubSensorConfiguration) {
  if (isSensorConfiguration(sensor)) {
    return sensor.sensorAltitude;
  }
  return sensor.altitude;
}
